import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  RefreshCw, Download, Zap, ExternalLink, ImageOff, ChevronLeft, ChevronRight,
  Loader2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import MissingImageDialog from "./MissingImageDialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const ISSUE_CATEGORIES = [
  ["missing_meta", "Missing meta description"],
  ["generic_meta", "Generic site description (wrong on products)"],
  ["duplicate_titles", "Duplicate titles"],
  ["thin_content", "Thin content"],
  ["missing_image", "Missing image"],
  ["missing_alt", "Missing ALT text"],
  ["missing_brand", "Missing brand"],
  ["missing_category", "Missing category"],
  ["index_ready", "Index ready"],
  ["in_sitemap", "In sitemap"],
  ["noindex", "No-index"],
];
const CAT_LABEL = Object.fromEntries(ISSUE_CATEGORIES);
const PRIO_STYLE = { P1: "bg-emerald-100 text-emerald-700", P2: "bg-sky-100 text-sky-700", P3: "bg-amber-100 text-amber-700", HOLD: "bg-slate-200 text-slate-600" };
const STATUS_STYLE = { INDEX_READY: "bg-emerald-100 text-emerald-700", IMPROVEMENT_REQUIRED: "bg-amber-100 text-amber-700", NOINDEX_RECOMMENDED: "bg-slate-200 text-slate-600" };

export default function IssuesCentre({ initialCategory = "missing_meta", onJobStarted }) {
  const [category, setCategory] = useState(initialCategory);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [sort, setSort] = useState("priority");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [allFiltered, setAllFiltered] = useState(false);
  const [imgProduct, setImgProduct] = useState(null);
  const [launching, setLaunching] = useState(false);

  useEffect(() => { setCategory(initialCategory); setPage(1); setSel(new Set()); setAllFiltered(false); }, [initialCategory]);

  const load = useCallback(() => {
    setData(null);
    api.get(`/admin/seo/issues/${category}`, { params: { q, sort, page, page_size: 50 } })
      .then((r) => setData(r.data)).catch(() => setData(false));
  }, [category, q, sort, page]);
  useEffect(() => { load(); }, [load]);

  const changeCat = (c) => { setCategory(c); setPage(1); setSel(new Set()); setAllFiltered(false); };
  const applySearch = () => { setQ(qInput); setPage(1); setSel(new Set()); setAllFiltered(false); };

  const toggle = (id) => {
    setAllFiltered(false);
    setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const selectPage = () => {
    setAllFiltered(false);
    setSel((s) => { const n = new Set(s); const all = data.items.every((it) => n.has(it.id)); data.items.forEach((it) => all ? n.delete(it.id) : n.add(it.id)); return n; });
  };
  const pageAllChecked = data?.items?.length > 0 && data.items.every((it) => sel.has(it.id));
  const selectedCount = allFiltered ? (data?.total || 0) : sel.size;

  const setFlag = async (id, field, value) => {
    try {
      await api.post("/admin/seo/bulk-indexing", { category, scope: "ids", ids: [id], field, value });
      setData((d) => ({ ...d, items: d.items.map((it) => it.id === id ? { ...it, [field]: value } : it) }));
    } catch { toast.error("Could not save"); }
  };

  const bulkIndexing = async (field, value) => {
    const scope = allFiltered ? "filtered" : "ids";
    const body = { category, field, value, scope, q, ids: Array.from(sel) };
    const n = selectedCount;
    if (value === "noindex" && !window.confirm(`Set NOINDEX on ${n.toLocaleString()} products? They will be removed from indexing.`)) return;
    try { const r = await api.post("/admin/seo/bulk-indexing", body); toast.success(`Updated ${r.data.updated.toLocaleString()} products`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bulk update failed"); }
  };

  const launchMetaJob = async () => {
    const scope = allFiltered ? "all" : "ids";
    const body = { type: "meta", category, scope, q, ids: Array.from(sel), priority_order: true };
    const n = selectedCount;
    if (!window.confirm(`Start a background AI job to generate meta descriptions for ${n.toLocaleString()} products?\n\nPriority 1 & 2 use ChatGPT; the long tail uses accurate product-fact templates. Approval-first with per-job rollback. You can leave this page.`)) return;
    setLaunching(true);
    try {
      const r = await api.post("/admin/seo/jobs", body);
      toast.success(`Job started — ${r.data.total.toLocaleString()} products (~${r.data.ai_estimate.toLocaleString()} AI). Track it in the Jobs tab.`);
      onJobStarted && onJobStarted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start job"); }
    finally { setLaunching(false); }
  };

  const exportCsv = () => {
    const url = `${BACKEND}/api/admin/seo/issues/${category}/export?q=${encodeURIComponent(q)}`;
    fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("acp_token")}` } })
      .then((r) => r.blob()).then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b); a.download = `seo-${category}.csv`; a.click();
      }).catch(() => toast.error("Export failed"));
  };

  return (
    <div className="space-y-4" data-testid="issues-centre">
      <div className="flex items-center gap-2 flex-wrap">
        <select data-testid="issue-category" value={category} onChange={(e) => changeCat(e.target.value)} className="h-9 rounded-md border px-3 text-sm bg-white font-semibold">
          {ISSUE_CATEGORIES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div className="flex items-center gap-1">
          <Input value={qInput} onChange={(e) => setQInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applySearch()} placeholder="Search name / SKU / brand" className="h-9 w-56" data-testid="issue-search" />
          <Button size="sm" variant="outline" onClick={applySearch}>Search</Button>
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 rounded-md border px-2 text-sm bg-white">
          <option value="priority">Sort: Priority</option><option value="name">Sort: Name</option><option value="sku">Sort: SKU</option>
        </select>
        <Button size="sm" variant="outline" onClick={load} data-testid="issue-refresh"><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
        <Button size="sm" variant="outline" onClick={exportCsv} data-testid="issue-export"><Download size={14} className="mr-1.5" /> Export CSV</Button>
      </div>

      {data && data !== false && (
        <div className="flex items-center gap-3 flex-wrap text-sm">
          <span className="font-semibold text-steel" data-testid="issue-total">{data.total.toLocaleString()} affected · {CAT_LABEL[category]}</span>
          <button onClick={selectPage} className="text-steel underline" data-testid="select-page">{pageAllChecked ? "Clear page" : "Select page"}</button>
          {data.total > data.items.length && (
            <button onClick={() => { setAllFiltered((v) => !v); setSel(new Set()); }} className={`underline ${allFiltered ? "text-accent font-semibold" : "text-steel"}`} data-testid="select-all-filtered">
              {allFiltered ? "✓ All affected selected" : `Select all ${data.total.toLocaleString()} affected`}
            </button>
          )}
          {selectedCount > 0 && <span className="rounded-full bg-steel text-white px-2.5 py-0.5 text-xs" data-testid="selected-count">{selectedCount.toLocaleString()} selected</span>}
        </div>
      )}

      {selectedCount > 0 && (
        <div className="rounded-xl border bg-amber-50/60 p-3 flex items-center gap-2 flex-wrap" data-testid="bulk-bar">
          {(category === "missing_meta" || category === "generic_meta") && (
            <Button size="sm" className="bg-accent text-steel hover:bg-amber-600 font-semibold" onClick={launchMetaJob} disabled={launching} data-testid="bulk-ai-meta">
              {launching ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Zap size={14} className="mr-1.5" />} AI Generate meta ({selectedCount.toLocaleString()})
            </Button>
          )}
          <span className="text-xs text-muted-foreground ml-1">Indexing:</span>
          <Button size="sm" variant="outline" onClick={() => bulkIndexing("seo_index", "index")}>Index</Button>
          <Button size="sm" variant="outline" onClick={() => bulkIndexing("seo_index", "noindex")} data-testid="bulk-noindex">Noindex</Button>
          <Button size="sm" variant="outline" onClick={() => bulkIndexing("seo_index", "auto")}>Auto</Button>
          <span className="text-xs text-muted-foreground ml-1">Sitemap:</span>
          <Button size="sm" variant="outline" onClick={() => bulkIndexing("seo_in_sitemap", "yes")}>Include</Button>
          <Button size="sm" variant="outline" onClick={() => bulkIndexing("seo_in_sitemap", "no")}>Exclude</Button>
        </div>
      )}

      {data === false ? <p className="text-sm text-destructive">Could not load issues.</p>
        : !data ? <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>
        : data.items.length === 0 ? <div className="rounded-xl border bg-white p-10 text-center text-sm text-muted-foreground">No products in this category.</div>
        : (
          <div className="rounded-xl border bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="issues-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-3 w-8"><input type="checkbox" checked={pageAllChecked} onChange={selectPage} data-testid="head-checkbox" /></th>
                  <th className="p-3">Product</th><th className="p-3">Priority</th><th className="p-3">Index status</th>
                  <th className="p-3">Indexing</th><th className="p-3">Sitemap</th><th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => (
                  <tr key={it.id} className="border-t align-top" data-testid={`issue-row-${it.sku}`}>
                    <td className="p-3"><input type="checkbox" checked={allFiltered || sel.has(it.id)} disabled={allFiltered} onChange={() => toggle(it.id)} data-testid={`row-check-${it.sku}`} /></td>
                    <td className="p-3">
                      <div className="flex items-start gap-2">
                        {it.image_url ? <img src={it.image_url} alt="" className="w-9 h-9 rounded object-contain border shrink-0" />
                          : <span className="w-9 h-9 rounded border grid place-items-center text-slate-300 shrink-0"><ImageOff size={16} /></span>}
                        <div className="min-w-0">
                          <div className="font-semibold text-steel line-clamp-1 max-w-xs">{it.name}</div>
                          <div className="font-mono text-xs text-muted-foreground">{it.sku} · {it.brand || "—"} · {it.category || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${PRIO_STYLE[it.priority]}`}>{it.priority}</span></td>
                    <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[it.index_status]}`}>{it.index_status.replace(/_/g, " ")}</span></td>
                    <td className="p-3">
                      <select value={it.seo_index} onChange={(e) => setFlag(it.id, "seo_index", e.target.value)} className="h-8 rounded border px-2 text-xs bg-white" data-testid={`row-index-${it.sku}`}>
                        <option value="auto">AUTO</option><option value="index">INDEX</option><option value="noindex">NOINDEX</option>
                      </select>
                    </td>
                    <td className="p-3">
                      <select value={it.seo_in_sitemap} onChange={(e) => setFlag(it.id, "seo_in_sitemap", e.target.value)} className="h-8 rounded border px-2 text-xs bg-white" data-testid={`row-sitemap-${it.sku}`}>
                        <option value="auto">AUTO</option><option value="yes">YES</option><option value="no">NO</option>
                      </select>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5">
                        {category === "missing_image" && <Button size="sm" variant="outline" className="h-8 px-2.5" onClick={() => setImgProduct(it)} data-testid={`manage-image-${it.sku}`}>Manage image</Button>}
                        <a href={`/product/${it.slug || it.id}`} target="_blank" rel="noreferrer" className="text-steel hover:text-accent p-1"><ExternalLink size={16} /></a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {data && data !== false && data.pages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {data.page} of {data.pages.toLocaleString()}</span>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev"><ChevronLeft size={14} /></Button>
            <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="page-next"><ChevronRight size={14} /></Button>
          </div>
        </div>
      )}

      {imgProduct && <MissingImageDialog product={imgProduct} onClose={() => setImgProduct(null)} onDone={load} />}
    </div>
  );
}
